import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, RefreshCw, Sparkles, AlertTriangle, RotateCcw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { refineText, track } from "@/lib/api";
import { track as rybbit } from "@/lib/track";

const PLATFORM_TABS = [
  { key: "general", label: "Allgemein" },
  { key: "kleinanzeigen", label: "Kleinanzeigen" },
  { key: "facebook", label: "Facebook" },
  { key: "vinted", label: "Vinted" },
];

const CopyButton = ({ text, testId, label = "Kopieren", full = false, variant = "outline" }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      toast.success("Kopiert", { description: "Bereit zum Einfügen" });
      track("copy_description");
      rybbit("result_action", { action: "copy" });
      setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      toast.error("Kopieren nicht möglich");
    }
  };
  return (
    <Button type="button" variant={copied ? "default" : variant} size={full ? "default" : "sm"} onClick={onCopy} data-testid={testId} className={`${full ? "w-full h-11 rounded-[0.85rem]" : "rounded-lg"} active:scale-[0.98]`}>
      {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Copy className="h-4 w-4 mr-1.5" />}
      {copied ? "Kopiert" : label}
    </Button>
  );
};

export const ResultView = ({ result, setResult, sessionId, category, initialPlatform = "general", aiError, onReset }) => {
  const variants = result.platformVariants || {};
  const availableTabs = PLATFORM_TABS.filter((t) => t.key !== "vinted" || !!variants.vinted);
  const [activePlatform, setActivePlatform] = useState(
    availableTabs.find((t) => t.key === initialPlatform) ? initialPlatform : "general"
  );
  const [refining, setRefining] = useState(false);

  const activeText = variants[activePlatform] || result.description || "";

  const doRefine = async (mode) => {
    setRefining(true);
    track("regenerate_clicked", { mode });
    try {
      const data = await refineText({
        sessionId,
        mode,
        platform: activePlatform,
        category,
        currentResult: result,
      });
      setResult(data.result);
      toast.success("Neue Version erstellt", {
        description: data.regenerationsRemaining > 0
          ? `Noch ${data.regenerationsRemaining} Änderung(en) möglich`
          : "Maximale Anzahl an Änderungen erreicht",
      });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      toast.error(status === 429 ? (detail || "Limit für Änderungen erreicht") : (detail || "Änderung nicht möglich"));
    } finally {
      setRefining(false);
    }
  };

  const copyAll = async () => {
    const all = `${result.titles?.[0] || ""}\n\n${activeText}`;
    try {
      await navigator.clipboard.writeText(all);
      toast.success("Alles kopiert", { description: "Titel + Beschreibung bereit zum Einfügen" });
      track("copy_all");
      rybbit("result_action", { action: "copy" });
    } catch (e) {
      toast.error("Kopieren nicht möglich");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-xl font-semibold">Dein Text ist fertig.</h3>
          <p className="text-sm text-muted-foreground">Prüfe die Angaben und kopiere den Text.</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onReset} data-testid="result-reset-button"><RotateCcw className="h-4 w-4 mr-1.5" /> Neu</Button>
      </div>

      {aiError && (
        <Alert data-testid="result-ai-fallback">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Einfacher Text</AlertTitle>
          <AlertDescription>Die KI war nicht verfügbar. Dieser Text wurde aus deinen Angaben erstellt – bitte vor dem Veröffentlichen prüfen.</AlertDescription>
        </Alert>
      )}

      {result.warnings?.length > 0 && (
        <Alert className="border-amber-300 bg-amber-50" data-testid="result-warnings">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Bitte vor Veröffentlichung prüfen</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4 space-y-1 text-amber-800/90 text-sm mt-1">
              {result.warnings.slice(0, 4).map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Title suggestions */}
      <div>
        <p className="text-sm font-medium mb-2 font-heading">Titelvorschläge</p>
        <div className="space-y-2">
          {(result.titles || []).map((t, i) => (
            <div key={i} className="flex items-center gap-2 rounded-xl border bg-card p-2.5">
              <span className="flex-1 text-sm">{t}</span>
              <CopyButton text={t} testId={`result-copy-title-${i}`} label="" variant="ghost" />
            </div>
          ))}
        </div>
      </div>

      {/* Platform tabs + description */}
      <div>
        <Tabs value={activePlatform} onValueChange={setActivePlatform}>
          <TabsList className={`grid w-full`} style={{ gridTemplateColumns: `repeat(${availableTabs.length}, minmax(0, 1fr))` }} data-testid="result-platform-tabs">
            {availableTabs.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`result-platform-tab-${t.key}`} className="text-xs sm:text-sm">{t.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {!variants.vinted && (
          <p className="text-xs text-muted-foreground mt-2">Vinted ist für diese Kategorie wahrscheinlich nicht passend.</p>
        )}
        <Card className="mt-3 p-4 border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium font-heading">Beschreibung</span>
            <CopyButton text={activeText} testId="result-copy-description-button" />
          </div>
          <p className="prose-listing text-sm text-foreground" data-testid="result-description-text">{activeText}</p>
        </Card>
      </div>

      {/* Short description */}
      <Card className="p-4 border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium font-heading">Kurzbeschreibung</span>
          <CopyButton text={result.shortDescription} testId="result-copy-short-button" />
        </div>
        <p className="prose-listing text-sm text-muted-foreground">{result.shortDescription}</p>
      </Card>

      {/* Reply templates */}
      {result.replyTemplates?.length > 0 && (
        <div>
          <p className="text-sm font-medium mb-2 font-heading">Antwortvorlagen für Interessenten</p>
          <div className="grid gap-2">
            {result.replyTemplates.map((r, i) => (
              <div key={i} className="flex items-start gap-2 rounded-xl border bg-card p-2.5">
                <span className="flex-1 text-sm text-muted-foreground">{r}</span>
                <CopyButton text={r} testId={`result-copy-reply-${i}`} label="" variant="ghost" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="space-y-3 pt-2">
        <Button className="w-full h-11 rounded-[0.85rem]" data-testid="result-copy-all-button" onClick={copyAll}>
          <Copy className="h-4 w-4 mr-2" /> Alles kopieren
        </Button>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" disabled={refining} onClick={() => doRefine("shorter")} data-testid="result-refine-shorter-button">{refining ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kürzer"}</Button>
          <Button variant="outline" disabled={refining} onClick={() => doRefine("friendlier")} data-testid="result-refine-friendlier-button">Freundlicher</Button>
          <Button variant="outline" disabled={refining} onClick={() => doRefine("factual")} data-testid="result-refine-more-factual-button">Sachlicher</Button>
          <Button variant="outline" disabled={refining} onClick={() => doRefine("regenerate")} data-testid="result-refine-new-version-button"><RefreshCw className="h-4 w-4 mr-1.5" /> Neue Version</Button>
        </div>
      </div>
    </div>
  );
};
